import {createFileRoute} from "@tanstack/react-router";
import {listCustomers} from "~/domainModels/ottokart/customers";
import {
    getProducts,
    listProductBrands,
    listProductCategories,
    listProductTypes,
    loadOttokartVariantsForDisplay,
    type Product,
    type ProductBrand,
    type ProductCategory,
    type ProductType,
    type Variant,
} from "~/domainModels/ottokart/products";
import {getQuotationById} from "~/domainModels/ottokart/quotations";
import {uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import {ErrorComponent} from "./-errorComponent";
import {NotFoundComponent} from "./-notFoundComponent";
import {PendingComponent} from "./-pendingComponent";
import {RouteComponent} from "./-routeComponent";

export const Route = createFileRoute("/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/ottokart/_ottokartScaffold/admin/quotations/$quotationId")({
    validateSearch: (search: Record<string, unknown>) => ({
        offset: typeof search.offset === "number" ? search.offset : 0,
        limit: typeof search.limit === "number" ? search.limit : 20,
    }),
    loader: async ({context, params}) => {
        const quotationIdResult = uuidZodValidator.safeParse(params.quotationId);
        if (!quotationIdResult.success) {
            throw new Error("Invalid quotation ID");
        }

        const {intellsysBaseUrl} = context;
        const workspaceId = context.workspace.id;
        const accessToken = context.authenticationStatus.accessToken;

        const [quotationResult, variants, productsResult, productTypesResult, productBrandsResult, productCategoriesResult, customersResult] =
            await Promise.all([
                getQuotationById({
                    intellsysBaseUrl,
                    workspaceId,
                    accessToken,
                    quotationId: quotationIdResult.data,
                }),
                loadOttokartVariantsForDisplay({intellsysBaseUrl, workspaceId, accessToken}),
                getProducts({intellsysBaseUrl, workspaceId, accessToken}),
                listProductTypes({intellsysBaseUrl, workspaceId, accessToken}),
                listProductBrands({intellsysBaseUrl, workspaceId, accessToken}),
                listProductCategories({intellsysBaseUrl, workspaceId, accessToken}),
                listCustomers({intellsysBaseUrl, workspaceId, accessToken, listParams: {isVerified: true}}),
            ]);

        if (quotationResult.success !== true) {
            throw quotationResult.error;
        }
        if (productsResult.success !== true) {
            throw productsResult.error;
        }
        if (productTypesResult.success !== true) {
            throw productTypesResult.error;
        }
        if (productBrandsResult.success !== true) {
            throw productBrandsResult.error;
        }
        if (productCategoriesResult.success !== true) {
            throw productCategoriesResult.error;
        }
        if (customersResult.success !== true) {
            throw customersResult.error;
        }

        const variantsById = new Map<string, Variant>(variants.map((variant) => [variant.id, variant]));
        const productsById = new Map<string, Product>(productsResult.data.map((product) => [product.id, product]));
        const productTypesById = new Map<string, ProductType>(productTypesResult.data.map((type) => [type.id, type]));
        const productBrandsById = new Map<string, ProductBrand>(productBrandsResult.data.map((brand) => [brand.id, brand]));
        const productCategoriesById = new Map<string, ProductCategory>(productCategoriesResult.data.map((category) => [category.id, category]));
        const customersById = new Map(customersResult.data.map((customer) => [customer.id, customer]));

        return {
            quotation: quotationResult.data,
            variantsById,
            productsById,
            productTypesById,
            productBrandsById,
            productCategoriesById,
            customersById,
        };
    },
    component: RouteComponent,
    errorComponent: ErrorComponent,
    pendingComponent: PendingComponent,
    notFoundComponent: NotFoundComponent,
});
